import { useEffect, useState } from "react";
import { X, FileText, AlertTriangle, CheckCircle2, User, MapPin, Hash, Mail, Phone, Calendar, Plus, Euro, AlertCircle, Pencil, Save } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import CountrySelect, { useCountries, validateBillingAddress } from "./CountrySelect";

/**
 * Preview the Fatura-Recibo BEFORE marking the booking as paid.
 *
 * The admin sees exactly what will be sent to Facturalusa — customer
 * (with warnings for missing NIF / address / postal code), line items,
 * total and observations. Confirming triggers the parent's onConfirm
 * callback (typically calling `PATCH /admin/bookings/{id}/payment` which
 * auto-emits the invoice).
 */
export default function InvoicePreviewModal({ bookingId, onClose, onConfirm }) {
  const countries = useCountries();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [edit, setEdit] = useState({
    name: "", email: "", phone: "",
    nif: "", address: "", city: "", postal_code: "", country: "Portugal",
    scheduled_date: "", scheduled_time: "",
    extras: [], extras_overrides: {}, total: "",
  });

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/facturalusa/preview/${bookingId}`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPreview(); /* eslint-disable-next-line */ }, [bookingId]);

  function startEdit() {
    // Seed the form with the current values, falling back to empty when
    // they are the system-generated placeholders so the admin can spot
    // them as blank inputs and fill them in cleanly.
    setEdit({
      name: preview.customer.name === "Cliente" ? "" : (preview.customer.name || ""),
      email: preview.customer.email || "",
      phone: preview.customer.phone || "",
      nif: preview.customer.nif_is_generic ? "" : preview.customer.nif,
      address: preview.customer.address_is_placeholder ? "" : preview.customer.address,
      city: preview.customer.city || "",
      postal_code: preview.customer.postal_code_is_placeholder ? "" : preview.customer.postal_code,
      country: preview.customer.country || "Portugal",
      scheduled_date: preview.scheduled_date || "",
      scheduled_time: preview.scheduled_time || "",
      extras: [...(preview.booking_extras || [])],
      extras_overrides: { ...(preview.booking_extras_overrides || {}) },
      total: String(preview.total ?? ""),
    });
    setEditing(true);
  }

  function toggleExtra(eid) {
    setEdit(e => {
      const present = e.extras.includes(eid);
      return {
        ...e,
        extras: present ? e.extras.filter(x => x !== eid) : [...e.extras, eid],
      };
    });
  }

  function setExtraPrice(eid, price) {
    setEdit(e => {
      const ovr = { ...e.extras_overrides };
      if (price === "" || price === null || price === undefined) {
        delete ovr[eid];
      } else {
        ovr[eid] = price;
      }
      return { ...e, extras_overrides: ovr };
    });
  }

  async function saveClientFiscal() {
    const addrErr = validateBillingAddress(edit, countries);
    if (addrErr) { toast.error(addrErr); return; }
    setSavingEdit(true);
    try {
      // (1) Booking-level corrections — cleaning date/time, extras, total,
      //     and guest fallbacks when there's no real client account.
      const bookingPatch = {
        scheduled_date: edit.scheduled_date,
        scheduled_time: edit.scheduled_time,
        extras: edit.extras,
        extras_overrides: Object.fromEntries(
          Object.entries(edit.extras_overrides)
            .filter(([, v]) => v !== "" && v !== null && v !== undefined)
            .map(([k, v]) => [k, Number(v) || 0])
        ),
      };
      // Only send total when the admin actually typed something.
      const trimmedTotal = String(edit.total ?? "").trim();
      if (trimmedTotal !== "" && !Number.isNaN(Number(trimmedTotal))) {
        bookingPatch.total = Number(trimmedTotal);
      }
      // For walk-ins or guest-only bookings we also persist the customer
      // fields on the booking itself so the next preview reflects them.
      if (preview.is_walk_in || !preview.client_id) {
        Object.assign(bookingPatch, {
          guest_name: edit.name,
          guest_email: edit.email,
          guest_phone: edit.phone,
          guest_nif: edit.nif,
          guest_address: edit.address,
          guest_city: edit.city,
          guest_postal_code: edit.postal_code,
          guest_country: edit.country,
        });
      }
      await api.patch(`/admin/bookings/${preview.booking_id}/fiscal-details`, bookingPatch);

      // (2) When there IS a real registered client (NOT a walk-in), also
      //     update the user profile so future invoices for them use these
      //     values automatically.
      if (preview.client_id && !preview.is_walk_in) {
        await api.put(`/admin/users/${preview.client_id}`, {
          name: edit.name || undefined,
          email: edit.email || undefined,
          phone: edit.phone,
          nif: edit.nif,
          address: edit.address,
          city: edit.city,
          postal_code: edit.postal_code,
          country: edit.country,
        });
      }
      toast.success("Dados atualizados.");
      setEditing(false);
      await fetchPreview();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleConfirm(opts) {
    setConfirming(true);
    try {
      await onConfirm?.(opts);
    } finally {
      setConfirming(false);
    }
  }

  const hasErrors = (preview?.warnings || []).some(w => w.level === "error");
  const hasWarnings = (preview?.warnings || []).some(w => w.level === "warning");

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="invoice-preview-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-[#062c27] border border-[#1a4640] max-w-2xl w-full p-6 space-y-5 my-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="eyebrow">Pré-visualização</div>
            <h2 className="font-display text-2xl text-[#fdfbf7] mt-1 inline-flex items-center gap-2">
              <FileText size={22} className="text-[#d4af37]" />
              Fatura-Recibo
            </h2>
            <p className="text-xs text-[#a0b2af] mt-1.5 max-w-md">
              Confirme os dados antes de emitir. Esta fatura será enviada à
              Facturalusa e ao cliente assim que confirmar.
            </p>
          </div>
          <button onClick={onClose} className="text-[#a0b2af] hover:text-white">
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="py-12 text-center text-sm text-[#a0b2af]">
            A carregar pré-visualização…
          </div>
        )}

        {!loading && preview && (
          <>
            {/* Already issued warning */}
            {preview.already_issued && (
              <div className="border border-[#10b981]/40 bg-[#10b981]/10 p-3 rounded-sm text-sm text-[#a7f3d0] flex items-start gap-2">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <div>
                  Esta reserva já tem fatura emitida ({preview.existing?.invoice_number || "—"}).
                  Marcar como pago não irá criar uma nova fatura.
                </div>
              </div>
            )}

            {/* Warnings panel */}
            {preview.warnings.length > 0 && (
              <div
                className={`border p-3 rounded-sm space-y-1.5 ${
                  hasErrors
                    ? "border-[#ef4444]/40 bg-[#ef4444]/10"
                    : "border-[#f59e0b]/40 bg-[#f59e0b]/10"
                }`}
                data-testid="invoice-preview-warnings"
              >
                {preview.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    {w.level === "error"
                      ? <AlertCircle size={14} className="text-[#fca5a5] mt-0.5 shrink-0" />
                      : <AlertTriangle size={14} className="text-[#fcd34d] mt-0.5 shrink-0" />}
                    <span className={w.level === "error" ? "text-[#fecaca]" : "text-[#fde68a]"}>
                      {w.message}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Customer */}
            <section className="border border-[#1a4640] p-4 rounded-sm space-y-2">
              <div className="flex items-center justify-between mb-2">
                <div className="eyebrow text-[#d4af37] flex items-center gap-2">
                  <User size={12} /> Cliente
                </div>
                {!editing && (
                  <button
                    onClick={startEdit}
                    className="text-[10px] uppercase tracking-widest text-[#d4af37] hover:text-[#f5d76e] inline-flex items-center gap-1"
                    data-testid="invoice-preview-edit-client"
                    title="Editar dados antes de emitir"
                  >
                    <Pencil size={11} /> Editar dados
                  </button>
                )}
              </div>
              {!editing ? (
                <>
                  <Field icon={User} label="Nome" value={preview.customer.name} />
                  <Field icon={Mail} label="Email" value={preview.customer.email || "—"} muted={!preview.customer.email} />
                  {preview.customer.phone && <Field icon={Phone} label="Telefone" value={preview.customer.phone} />}
                  <Field
                    icon={Hash}
                    label="NIF"
                    value={preview.customer.nif}
                    badge={preview.customer.nif_is_generic ? "consumidor final" : null}
                  />
                  <Field
                    icon={MapPin}
                    label="Morada"
                    value={`${preview.customer.address}, ${preview.customer.postal_code} ${preview.customer.city} · ${preview.customer.country}`}
                    badge={preview.customer.address_is_placeholder ? "placeholder" : null}
                  />
                  <Field
                    icon={Calendar}
                    label="Data da limpeza"
                    value={`${preview.scheduled_date || "—"}${preview.scheduled_time ? ` · ${preview.scheduled_time}` : ""}`}
                  />
                </>
              ) : (
                <div className="space-y-3" data-testid="invoice-preview-edit-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <InlineInput label="Nome / Razão social" value={edit.name}
                      onChange={v => setEdit(e => ({ ...e, name: v }))} testId="ip-edit-name" />
                    <InlineInput label="Email" value={edit.email}
                      onChange={v => setEdit(e => ({ ...e, email: v }))} testId="ip-edit-email" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <InlineInput label="Telefone" value={edit.phone}
                      onChange={v => setEdit(e => ({ ...e, phone: v }))} testId="ip-edit-phone" />
                    <InlineInput label="NIF" value={edit.nif}
                      onChange={v => setEdit(e => ({ ...e, nif: v }))} testId="ip-edit-nif" />
                  </div>
                  <InlineInput label="Morada (rua, nº)" value={edit.address}
                    onChange={v => setEdit(e => ({ ...e, address: v }))} testId="ip-edit-address" />
                  <div className="grid sm:grid-cols-3 gap-3">
                    <InlineInput label="Localidade" value={edit.city}
                      onChange={v => setEdit(e => ({ ...e, city: v }))} testId="ip-edit-city" />
                    <InlineInput label="Código postal" value={edit.postal_code}
                      onChange={v => setEdit(e => ({ ...e, postal_code: v }))} testId="ip-edit-postal" />
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">País</span>
                      <CountrySelect value={edit.country} onChange={v => setEdit(e => ({ ...e, country: v }))} testId="ip-edit-country"
                        className="mt-1 w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none px-3 py-2 text-sm text-[#fdfbf7] rounded-sm" />
                    </label>
                  </div>
                  <div className="pt-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2 inline-flex items-center gap-1.5">
                      <Calendar size={11} /> Data efetiva da limpeza
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <InlineInput label="Data (YYYY-MM-DD)" value={edit.scheduled_date}
                        onChange={v => setEdit(e => ({ ...e, scheduled_date: v }))}
                        placeholder="2026-06-15"
                        testId="ip-edit-scheduled-date" />
                      <InlineInput label="Hora (HH:MM)" value={edit.scheduled_time}
                        onChange={v => setEdit(e => ({ ...e, scheduled_time: v }))}
                        placeholder="10:00"
                        testId="ip-edit-scheduled-time" />
                    </div>
                    <p className="text-[10px] text-[#a0b2af] mt-1.5">
                      Esta data aparece na descrição da fatura emitida na Facturalusa.
                    </p>
                  </div>

                  {/* Extras (add/remove last-minute) */}
                  {(preview.extras_catalog || []).length > 0 && (
                    <div className="pt-1">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2 inline-flex items-center gap-1.5">
                        <Plus size={11} /> Extras de última hora
                      </div>
                      <div className="space-y-2 border border-[#1a4640] p-3 rounded-sm bg-[#031c18]">
                        {preview.extras_catalog.map(ex => {
                          const active = edit.extras.includes(ex.id);
                          const overridePrice = edit.extras_overrides[ex.id];
                          return (
                            <div key={ex.id} className="flex items-center gap-3" data-testid={`ip-extra-row-${ex.id}`}>
                              <label className="inline-flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                <input type="checkbox" checked={active}
                                       onChange={() => toggleExtra(ex.id)}
                                       data-testid={`ip-extra-toggle-${ex.id}`}
                                       className="w-4 h-4 accent-[#d4af37]" />
                                <span className={`text-sm ${active ? "text-[#fdfbf7]" : "text-[#a0b2af]"} truncate`}>
                                  {ex.name}
                                  {ex.price_on_request && (
                                    <span className="ml-1 text-[10px] uppercase tracking-widest text-[#a0b2af]">a combinar</span>
                                  )}
                                </span>
                              </label>
                              {active && (
                                <div className="flex items-center gap-1 shrink-0">
                                  <input
                                    type="number" step="0.01" min="0"
                                    value={overridePrice ?? ""}
                                    placeholder={`${ex.price.toFixed(2)}`}
                                    onChange={e => setExtraPrice(ex.id, e.target.value)}
                                    className="w-20 bg-[#062c27] border border-[#1a4640] focus:border-[#d4af37] outline-none px-2 py-1 text-sm text-[#fdfbf7] rounded-sm text-right"
                                    data-testid={`ip-extra-price-${ex.id}`}
                                  />
                                  <span className="text-xs text-[#a0b2af]">€</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-[#a0b2af] mt-1.5">
                        Adicione extras (ex.: lavandaria, manutenção) pedidos depois da reserva.
                        Deixe o preço vazio para usar o valor de catálogo.
                      </p>
                    </div>
                  )}

                  {/* Manual total override */}
                  <div className="pt-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2 inline-flex items-center gap-1.5">
                      <Euro size={11} /> Valor total da fatura
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" step="0.01" min="0"
                        value={edit.total}
                        placeholder="Recalcular automaticamente"
                        onChange={e => setEdit(es => ({ ...es, total: e.target.value }))}
                        className="flex-1 bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none px-3 py-2 text-sm text-[#fdfbf7] rounded-sm placeholder:text-[#3a5651]"
                        data-testid="ip-edit-total"
                      />
                      <span className="text-sm text-[#a0b2af]">€</span>
                      <button
                        type="button"
                        onClick={() => setEdit(es => ({ ...es, total: "" }))}
                        className="text-[10px] uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] px-2"
                        title="Limpar override — recalcula a partir das tipologias + extras"
                        data-testid="ip-edit-total-clear">
                        Recalcular
                      </button>
                    </div>
                    <p className="text-[10px] text-[#a0b2af] mt-1.5">
                      Deixe vazio para o sistema recalcular automaticamente a partir da
                      tipologia + extras. Preencher força este valor exato na fatura.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <button onClick={saveClientFiscal} disabled={savingEdit}
                            className="btn-gold px-4 py-1.5 text-[11px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                            data-testid="invoice-preview-edit-save">
                      <Save size={12} /> {savingEdit ? "A guardar…" : "Guardar e recarregar"}
                    </button>
                    <button onClick={() => setEditing(false)}
                            className="px-4 py-1.5 text-[11px] text-[#a0b2af] hover:text-white">
                      Cancelar edição
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Lines */}
            <section className="border border-[#1a4640] p-4 rounded-sm">
              <div className="eyebrow text-[#d4af37] mb-3 flex items-center gap-2">
                <FileText size={12} /> Linhas
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
                  <tr>
                    <th className="text-left pb-2">Descrição</th>
                    <th className="text-right pb-2 w-12">Qtd</th>
                    <th className="text-right pb-2 w-20">Preço</th>
                    <th className="text-right pb-2 w-20">Total</th>
                  </tr>
                </thead>
                <tbody className="text-[#fdfbf7]">
                  {preview.lines.map((l, i) => (
                    <tr key={i} className="border-t border-[#1a4640]">
                      <td className="py-2 text-xs whitespace-pre-line">{l.description}</td>
                      <td className="py-2 text-right">{l.quantity}</td>
                      <td className="py-2 text-right">{l.unit_price.toFixed(2)}€</td>
                      <td className="py-2 text-right">{l.total.toFixed(2)}€</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#d4af37]/40">
                    <td colSpan={3} className="pt-3 text-right text-xs uppercase tracking-widest text-[#a0b2af]">Total</td>
                    <td className="pt-3 text-right font-display text-xl text-[#d4af37]" data-testid="invoice-preview-total">
                      {preview.total.toFixed(2)}€
                    </td>
                  </tr>
                </tfoot>
              </table>
              <p className="mt-3 text-[10px] text-[#a0b2af] leading-relaxed">
                {preview.observations}
              </p>
            </section>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2 flex-wrap">
              <button
                onClick={() => handleConfirm()}
                disabled={confirming || hasErrors}
                className="btn-gold px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="invoice-preview-confirm"
                title={hasErrors ? "Resolva os erros antes de continuar" : "Marcar pago e emitir fatura"}
              >
                <CheckCircle2 size={14} />
                {confirming ? "A processar…" : preview.already_issued ? "Marcar como pago" : "Marcar pago + emitir & enviar"}
              </button>

              {/* Cash payment (no invoice) — only shown when the invoice
                  isn't already issued. The revenue still flows through to
                  the admin dashboard (payment_status=paid) but no
                  Facturalusa fatura-recibo is produced. */}
              {!preview.already_issued && (
                <button
                  onClick={() => {
                    if (!window.confirm(
                      "Marcar como PAGO EM DINHEIRO sem emitir fatura?\n\n" +
                      "A reserva fica registada como concluída e paga, o valor entra " +
                      "nas finanças do admin, mas NÃO será emitida fatura no Facturalusa.\n\n" +
                      "Use esta opção apenas quando o cliente pagou em dinheiro e " +
                      "explicitamente não pediu fatura."
                    )) return;
                    handleConfirm({ skipInvoice: true, paymentMethod: "cash" });
                  }}
                  disabled={confirming}
                  className="px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 border border-[#1a4640] hover:border-[#d4af37] text-[#fdfbf7] disabled:opacity-50 rounded-sm"
                  data-testid="invoice-preview-cash-no-invoice"
                  title="Cliente pagou em dinheiro e não pediu fatura — regista o pagamento sem emitir fatura"
                >
                  <Euro size={14} />
                  Pago em dinheiro · sem fatura
                </button>
              )}

              <button
                onClick={onClose}
                className="px-5 py-2.5 text-xs text-[#a0b2af] hover:text-white"
                data-testid="invoice-preview-cancel"
              >
                Cancelar
              </button>
              {hasWarnings && !hasErrors && (
                <span className="text-[11px] text-[#fcd34d]">
                  Pode continuar, mas considere completar os dados em falta antes.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, badge, muted = false }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={13} className="text-[#a0b2af] mt-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
        <div className={`text-sm break-words ${muted ? "text-[#a0b2af] italic" : "text-[#fdfbf7]"}`}>
          {value}
          {badge && (
            <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#f59e0b]/40 text-[#fcd34d] rounded-sm">
              {badge}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function InlineInput({ label, value, onChange, testId, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</span>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        className="mt-1 w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none px-3 py-2 text-sm text-[#fdfbf7] rounded-sm placeholder:text-[#3a5651]"
        data-testid={testId}
      />
    </label>
  );
}
